import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { getEnvironment } from 'app/appSettings';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { AuthorizationMatrix } from 'app/core/store/session/authorization/authorization.interface';
import { UserContext } from 'app/core/store/session/user-context/user-context.interface';
import { ContractRestrictions } from '../../core/store/contract-details/contract-details.interface';
import { LayoutBreakpoints } from '../../core/util/layout-breakpoints';

interface Module {
  translationCode: string;
  goto: string | (() => string);
  pathRegexs?: RegExp[];
  disabled?: boolean;
  visible: () => boolean;
  hideOnMobile?: boolean;
  selectedCustomerRequired: boolean;
}

@Component({
  selector: 'europool-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  animations: [
    trigger('modules', [
      transition('* => *', [
        query(':enter', [style({ opacity: 0, transform: 'translateY(-50px)' }), stagger(-100, [animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0px)' }))])], {
          optional: true,
        }),
        query(':leave', [stagger(-100, [animate('500ms ease-in', style({ opacity: 0, transform: 'translateY(-50px)' }))])], { optional: true }),
      ]),
    ]),
    trigger('usermenu', [
      transition(':enter', [style({ opacity: 0, transform: 'translateY(-50px)' }), animate('500ms 300ms ease-out', style({ opacity: 1, transform: 'translateY(0px)' }))]),
      transition(':leave', [animate('500ms ease-in', style({ opacity: 0, transform: 'translateY(50px)' }))]),
    ]),
  ],
})
export class HeaderComponent implements OnInit, OnChanges {
  private modules: Module[];
  private headerModules: Module[];
  openMobileMenu: boolean = false;

  public visibleModules: Module[];
  public visibleHeaderModules: Module[];
  public visibleMobileModules: Module[];
  public environment: string;

  @Input() firstName: string;
  @Input() initials: string;
  @Input() selectedCustomer: Definition;
  @Input() canSwitchCustomer: boolean;
  @Input() path: string;
  @Input() authorized: boolean; // can be undefined, false or true !!!
  @Input() contextLoading: boolean;
  @Input() customerInfoLoaded: boolean;
  @Input() contractDetailsLoading: boolean;
  @Input() authorization: AuthorizationMatrix;
  @Input() userContext: UserContext;
  @Input() contractRestrictions: ContractRestrictions;

  @Output() gotoDashboard: EventEmitter<void> = new EventEmitter<void>();
  @Output() openModule: EventEmitter<string> = new EventEmitter<string>();
  @Output() login: EventEmitter<void> = new EventEmitter<void>();
  @Output() logout: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectCustomer: EventEmitter<void> = new EventEmitter<void>();
  showSmallLogo$ = LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.minimumScreenOrLess);

  constructor(private titleService: Title, private breakpointObserver: BreakpointObserver) {
  }

  ngOnInit() {
    this.environment = getEnvironment();
    if (this.environment) {
      this.titleService.setTitle(`EPS ${this.environment}`);
    }
    // @formatter:off
    this.modules = [
      {
        translationCode: 'SHARED.HEADER.STOCK_COUNT',
        goto: '/stock-count',
        visible: () => this.authorization.STOCK.GET,
        selectedCustomerRequired: true,
      },
      {
        translationCode: 'SHARED.HEADER.ORDERS',
        goto: '/orders/clear',
        visible: () => this.isAuthorized('ORDERS'),
        pathRegexs: [/\/orders(?:\/.*)?/, /\/ccr(?:\/.*)?/],
        selectedCustomerRequired: true,
      },
      {
        translationCode: 'SHARED.HEADER.RELOCATIONS',
        goto: '/relocations',
        visible: () => this.authorized && this.userContext.canRelocate,
        selectedCustomerRequired: false,
      },
      {
        translationCode: 'SHARED.HEADER.FLOWS',
        goto: '/flows/clear',
        visible: () => this.isAuthorized('FLOWS'),
        pathRegexs: [/\/flows(?:\/.*)?/],
        selectedCustomerRequired: true,
      },
      {
        translationCode: 'SHARED.HEADER.CALENDAR',
        goto: '/calendar/order',
        visible: () => this.isAuthorized('CALENDAR'),
        hideOnMobile: true,
        selectedCustomerRequired: true,
      },
      {
        translationCode: 'SHARED.HEADER.INVOICES',
        goto: '/invoices',
        visible: () => this.isAuthorized('INVOICES'),
        pathRegexs: [/\/invoices(?:\/.*)?/],
        selectedCustomerRequired: true,
      },
      { translationCode: 'SHARED.HEADER.REPORT', goto: '/report', visible: () => this.isAuthorized('REPORTS'), selectedCustomerRequired: true, },
      {
        translationCode: 'SHARED.HEADER.BLANK',
        goto: '/blank-receipts',
        visible: () => this.isAuthorized('BLANK_RECEIPT') && this.contractRestrictions.blankReceiptPossible,
        selectedCustomerRequired: true,
      },
    ];

    this.headerModules = [
      {
        translationCode: 'ADMIN.SHARED.CLIENTUSERS',
        goto: '/admin/clientUsers',
        visible: () => this.userContext?.isAdmin,
        selectedCustomerRequired: false,
      },
      {
        translationCode: 'ADMIN.SHARED.EPSUSERS',
        goto: '/admin/epsUsers',
        visible: () => this.userContext?.isAdmin,
        selectedCustomerRequired: false,
      },
      {
        translationCode: 'ADMIN.I18N.LABEL',
        goto: '/admin/i18n',
        visible: () => this.userContext?.isAdmin,
        selectedCustomerRequired: false,
      },
      {
        translationCode: 'ADMIN.LOCAL_OFFICE.LABEL',
        goto: '/admin/local-office',
        visible: () => this.userContext?.salesOrganisations?.length > 0,
        selectedCustomerRequired: false,
      },
      { translationCode: 'SHARED.HEADER.FAQ', goto: '/faq', visible: () => true, selectedCustomerRequired: false },
      // { translationCode: 'SHARED.HEADER.CONTACT_US', goto: '/contact', visible: () => true },
      {
        translationCode: 'SHARED.HEADER.EMAIL_ACTORS',
        goto: '/email-actors',
        visible: () => this.customerInfoLoaded && this.userContext?.useEmailActors,
        selectedCustomerRequired: true,
      },
      {
        translationCode: 'SHARED.HEADER.HOME_MARKET_BONUS',
        goto: '/home-market-bonus',
        visible: () => this.customerInfoLoaded && this.userContext?.canAccessHomeMarketBonus,
        selectedCustomerRequired: true,
      },
    ];
    // @formatter:on
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    this.visibleModules = (this.modules || []).filter(module => module.visible() && this.loggedIn);
    this.visibleHeaderModules = (this.headerModules || []).filter(module => module.visible());
    this.visibleMobileModules = [...this.visibleModules, ...this.visibleHeaderModules].filter(module => !module.hideOnMobile);
  }

  get loggedIn(): boolean {
    return !!this.firstName || !!(this.selectedCustomer?.id);
  }

  moduleClicked(module: Module) {
    if (this.isEnabled(module)) {
      this.openModule.emit(this.getGoto(module));
    }
  }

  private getGoto(module: Module) {
    if (typeof module.goto === 'function') {
      return module.goto();
    }
    return module.goto;
  }

  closeMobileMenu() {
    if (this.openMobileMenu) {
      this.openMobileMenu = !this.openMobileMenu;
    }
  }

  isActive(module: Module, path: string) {
    return (
      path.indexOf(this.getGoto(module)) === 0 ||
      (module.pathRegexs &&
        module.pathRegexs.find(p => {
          const test = p.exec(path);
          return test && test.length === 1 && test[0] === path;
        }))
    );
  }

  private isAuthorized = screen => this.authorization && this.authorization.ACCESS[screen];

  isEnabled(module: Module): boolean {
    return !!(this.loggedIn && !module.disabled && (this.selectedCustomer || this.userContext.isSuperUser || !module.selectedCustomerRequired));
  }
}
